<template>
  <div aria-live="assertive" class="fixed inset-0 flex items-end px-4 py-6 z-50 pointer-events-none sm:p-6 sm:items-end">
    <div class="w-full flex flex-col items-center space-y-4">
      <transition
        enter-class="translate-y-[13em]"
        leave-class="translate-y-0"
        enter-to-class="translate-y-0"
        leave-to-class="translate-y-[13em]"
      >
        <div v-if="toast" class="max-w-md duration-200 w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 divide-x divide-gray-200">
          <div class="w-0 flex-1 flex items-center p-4">
            <div class="w-full">
              <div class="text-sm font-medium text-gray-900 flex space-x-2 items-center">
                <div v-if="type === 'error'" class="mx-auto bg-red-50 flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full sm:mx-0 sm:h-6 sm:w-6">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div v-else-if="type === 'info'" class="mx-auto bg-blue-50 flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full sm:mx-0 sm:h-6 sm:w-6">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div v-else-if="type === 'warning'" class="mx-auto bg-yellow-50 flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full sm:mx-0 sm:h-6 sm:w-6">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-yellow-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div v-else class="mx-auto bg-green-50 flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full sm:mx-0 sm:h-6 sm:w-6">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                </div>

                <p>{{ message }}</p>
              </div>
              <p v-if="description" class="mt-1 text-sm text-gray-500">{{ description }}</p>
            </div>
          </div>
          <div class="flex">
            <div v-if="confirm" class="flex flex-col divide-y divide-gray-200">
              <div class="h-0 flex-1 flex">
                <button @click="onSave" type="button" class="w-full space-x-1 border border-transparent rounded-none rounded-tr-lg px-4 py-3 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-primary-500 focus:outline-none focus:z-10 focus:ring-2 focus:ring-indigo-500">
                  <svg v-if="confirming" class="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{{ confirmMessage }}</span>
                </button>
              </div>
              <div v-if="cancelMessage" class="h-0 flex-1 flex">
                <button @click="onCancel" type="button" class="w-full border border-transparent rounded-none rounded-br-lg px-4 py-3 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {{ cancelMessage }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>

import { mapGetters } from "vuex"

export default {
  data() {
    return {
      confirming: false,
    }
  },
  methods: {
    async onSave() {
      this.confirming = true
      await this.$store.dispatch('toast/onSave')
      this.confirming = false
    },
    onCancel() {
      this.$store.dispatch('toast/onCancel')
    },
  },
  computed: {
    ...mapGetters({
      type: 'toast/type',
      toast: 'toast/toast',
      message: 'toast/message',
      description: 'toast/description',

      confirm: 'toast/confirm',
      confirmMessage: 'toast/confirmMessage',
      cancelMessage: 'toast/cancelMessage',
    }),
  },
}

</script>
